import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Box,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   Checkbox,
   Button,
   HStack,
   Text,
   useColorModeValue,
   Flex,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import {
   getPendingVoucherSalesAdjustment,
   getPendingSalesAdjustment,
   getPendingPurchaseAdjustment,
} from '../../../../redux/actions/salesInvoice';
import { getPendingVoucherPurchaseAdjustment } from '../../../../redux/actions/purchaseInvoice';
import { addManyOutAdjustments } from '../../../../redux/actions/voucher';
import moment from 'moment';
import { getPurchaseReturnInvoices } from '../../../../redux/actions/purchaseReturnInvoice';
import { getPurchaseInvoices } from '../../../../redux/actions/purchaseInvoice';
import { getSaleInvoices } from '../../../../redux/actions/salesInvoice';
import { getSaleReturnInvoices } from '../../../../redux/actions/salesReturnInvoice';
import Show from 'components/show/Show';

const TransactionDetailModal = ({
   isOpen,
   onClose,
   title = 'Transaction Details',
   size = '6xl',
   maxHeight = '90vh',
   companyId,
   accountId,
   invoiceData,
   type = '',
   page = 1,
   limit = 10,
}) => {
   const dispatch = useDispatch();
   const [selectedRows, setSelectedRows] = useState(new Set());
   const [selectAll, setSelectAll] = useState(false);
   const [isInitialLoad, setIsInitialLoad] = useState(true);
   const [pendingReturnAdjustment, setPendingReturnAdjustment] = useState(invoiceData?.pendingamt || 0);
   const [isLoading, setIsLoading] = useState(false);

   // Color mode values
   const bgColor = useColorModeValue('white', 'gray.800');
   const borderColor = useColorModeValue('gray.200', 'gray.600');
   const headerBg = useColorModeValue('gray.50', 'gray.700');
   const selectedRowBg = useColorModeValue('blue.50', 'blue.900');
   const hoverBg = useColorModeValue('gray.50', 'gray.700');

   const pendingVoucherSalesAdjustment = useSelector((state) => state?.sales?.pendingVoucherSalesAdjustment); // for sales only
   const pendingVoucherPurchaseAdjustment = useSelector((state) => state?.purchaseInvoice?.pendingPurchaseAdjustment); // for purchase only
   const pendingSalesAdjustment = useSelector((state) => state?.sales?.pendingSalesAdjustment); // for sales return only
   const pendingPurchaseAdjustment = useSelector((state) => state?.sales?.pendingPurchaseAdjustment); // for purchase return only

   const pendingAdjustment = useMemo(() => {
      // if (type.toUpperCase() === 'SALES' || type.toUpperCase() === 'SALES RETURN') return pendingVoucherSalesAdjustment;
      // if (type.toUpperCase() === 'PURCHASE' || type.toUpperCase() === 'PURCHASE RETURN')
      //    return pendingPurchaseAdjustment;

      if (type?.toUpperCase() === 'SALES') return pendingVoucherSalesAdjustment;
      if (type?.toUpperCase() === 'SALES RETURN') return pendingSalesAdjustment;
      if (type?.toUpperCase() === 'PURCHASE') return pendingVoucherPurchaseAdjustment;
      if (type?.toUpperCase() === 'PURCHASE RETURN') return pendingPurchaseAdjustment;
      return [];
   }, [
      pendingPurchaseAdjustment,
      pendingSalesAdjustment,
      pendingVoucherPurchaseAdjustment,
      pendingVoucherSalesAdjustment,
      type,
   ]);

   const newPendingAdjustment = useMemo(() => {
      let pendingAmt = invoiceData?.pendingamt || 0;
      const newSelected = new Set();
      return pendingAdjustment.map((item) => {
         let adjustAmt = 0;
         let adjustAmt1 = 0;
         if (pendingAmt > 0 && item?.pending_amount > pendingAmt) {
            adjustAmt = item?.pending_amount - pendingAmt;
            adjustAmt1 = item?.pending_amount - adjustAmt;
            pendingAmt = 0;
            newSelected.add(item.id);
            setSelectedRows(newSelected);
         }

         if (pendingAmt > 0 && item?.pending_amount <= pendingAmt) {
            adjustAmt = item?.pending_amount;
            adjustAmt1 = item?.pending_amount;
            pendingAmt -= item?.pending_amount;
            newSelected.add(item.id);
            setSelectedRows(newSelected);
         }

         return { ...item, adjusted_amount: adjustAmt1 };
      });
   }, [invoiceData?.pendingamt, pendingAdjustment]);

   const getPendingAdjustment = useCallback(async () => {
      if (isOpen && companyId && accountId && companyId > 0 && accountId > 0) {
         if (type?.toUpperCase() === 'SALES') {
            await dispatch(getPendingVoucherSalesAdjustment({ companyId, accountId }));
         }
         if (type?.toUpperCase() === 'SALES RETURN') {
            await dispatch(getPendingSalesAdjustment({ companyId, accountId }));
         }
         if (type?.toUpperCase() === 'PURCHASE') {
            await dispatch(getPendingVoucherPurchaseAdjustment({ companyId, accountId }));
         }
         if (type?.toUpperCase() === 'PURCHASE RETURN') {
            await dispatch(getPendingPurchaseAdjustment({ companyId, accountId }));
         }
      }
   }, [accountId, companyId, dispatch, isOpen, type]);

   // Load data when modal opens and when accountId/companyId changes
   useEffect(() => {
      // if (isOpen && companyId && accountId) {
      //    if (type.toUpperCase() === 'SALES' || type.toUpperCase() === 'SALES RETURN')
      //       dispatch(getPendingVoucherSalesAdjustment({ companyId, accountId }));
      //    if (type.toUpperCase() === 'PURCHASE' || type.toUpperCase() === 'PURCHASE RETURN')
      //       dispatch(getPendingVoucherPurchaseAdjustment({ companyId, accountId }));
      //    setIsInitialLoad(false);
      // }
      getPendingAdjustment();
   }, [getPendingAdjustment]);

   // Reset state when modal closes
   useEffect(() => {
      if (!isOpen) {
         setSelectedRows(new Set());
         setSelectAll(false);
      }
   }, [isOpen]);

   // Simplified columns configuration
   const columns = [
      { id: 'docNumber', header: 'Doc No', accessor: 'docNumber' },
      {
         id: 'transactionAt',
         header: 'Date',
         accessor: (item) => moment(item.docdate).format('DD/MM/YYYY'),
      },
      { id: 'doc_type', header: 'Doc Type', accessor: 'doc_type' },
      { id: 'amount', header: 'Net Amount', accessor: 'amount' },
      { id: 'total_adjusted', header: 'Adjusted Amount', accessor: 'total_adjusted' },
      { id: 'pending_amount', header: 'Pending Amount', accessor: 'pending_amount' },
      // { id: 'adjusted_amount', header: 'Adjusted Amount', accessor: 'adjusted_amount' },
   ];

   const outAdjustments = useMemo(() => {
      const pendingAmt = invoiceData?.pendingamt || 0;
      const newSelected = new Set(selectedRows);
      const isPurchase = type.toUpperCase() === 'PURCHASE';
      const isPurchaseReturn = type.toUpperCase() === 'PURCHASE RETURN';
      const isSales = type.toUpperCase() === 'SALES';
      const isSalesReturnDoc = type.toUpperCase() === 'SALES RETURN';
      let remainingAmt = pendingAmt;

      return newPendingAdjustment
         .filter((adjustment) => selectedRows.has(adjustment.id))
         .map((item) => {
            const itemPendingAmt = item.pending_amount || 0;
            const isSalesDoc = item.doc_type === 'SALES';
            const shouldSwap = isPurchase && isSalesDoc;
            let adjustedAmount = 0;

            if (remainingAmt <= 0) {
               return null;
            }

            if (itemPendingAmt > remainingAmt) {
               adjustedAmount = remainingAmt;
               remainingAmt = 0;
               newSelected.add(item.id);
            } else {
               adjustedAmount = itemPendingAmt;
               remainingAmt -= itemPendingAmt;
               newSelected.add(item.id);
            }
            if (newSelected.size !== selectedRows.size) {
               setSelectedRows(newSelected);
            }

            return {
               drDocHeaderId: shouldSwap ? item.id : (isSalesReturnDoc || isPurchaseReturn) ? item?.id : invoiceData.id,
               drDaybookId: shouldSwap ? item.daybookId : (isSalesReturnDoc || isPurchaseReturn) ? item?.daybookId : invoiceData.daybookId,
               amount: adjustedAmount,
               crDocHeaderId: shouldSwap ? invoiceData?.id : (isSalesReturnDoc || isPurchaseReturn) ? invoiceData.id : item.id,
               crDaybookId: shouldSwap
                  ? invoiceData.daybookId
                  : (isSalesReturnDoc || isPurchaseReturn)
                     ? invoiceData.daybookId
                     : item.daybookId,
            }
         })
         .filter(Boolean);
   }, [invoiceData?.daybookId, invoiceData?.id, invoiceData?.pendingamt, newPendingAdjustment, selectedRows, type]);

   const adjustedAmount = useMemo(() => {
      return outAdjustments.reduce((total, item) => total + Number(item?.amount || 0), 0);
   }, [outAdjustments]);

   const handleSelectAll = (e) => {
      setSelectAll(e.target.checked);
      if (e.target.checked && pendingAdjustment.length > 0) {
         setSelectedRows(new Set(pendingAdjustment.map((item) => item.id)));
      } else {
         setSelectedRows(new Set());
      }
   };

   const handleRowSelect = (id, checked) => {
      const newSelected = new Set(selectedRows);
      if (checked) {
         newSelected.add(id);
      } else {
         newSelected.delete(id);
      }
      setSelectedRows(newSelected);
      setSelectAll(newSelected.size === pendingAdjustment.length);
   };

   const getCellValue = (item, accessor) => {
      if (typeof accessor === 'function') {
         return accessor(item);
      }
      return item[accessor] || '-';
   };

   const handleSave = async () => {
      // onSave(outAdjustments);
      setIsLoading(true);
      await dispatch(addManyOutAdjustments({ outAdjustments }));
      if (type.toUpperCase() === 'SALES' || type.toUpperCase() === 'SALES RETURN')
         await dispatch(getPendingVoucherSalesAdjustment({ companyId, accountId }));
      if (type.toUpperCase() === 'PURCHASE' || type.toUpperCase() === 'PURCHASE RETURN')
         await dispatch(getPendingVoucherPurchaseAdjustment({ companyId, accountId }));

      if (type.toUpperCase() === 'PURCHASE')
         await dispatch(getPurchaseInvoices({ companyId, limit: limit, page: page, sortBy: 'id', sortOrder: 'DESC' }));

      if (type.toUpperCase() === 'PURCHASE RETURN')
         await dispatch(
            getPurchaseReturnInvoices({ companyId, limit: limit, page: page, sortBy: 'id', sortOrder: 'DESC' }),
         );

      if (type.toUpperCase() === 'SALES')
         await dispatch(getSaleInvoices({ companyId, limit: limit, page: page, sortBy: 'id', sortOrder: 'DESC' }));

      if (type.toUpperCase() === 'SALES RETURN')
         await dispatch(
            getSaleReturnInvoices({ companyId, limit: limit, page: page, sortBy: 'id', sortOrder: 'DESC' }),
         );
      setIsLoading(false);
      onClose();
   };

   useEffect(() => {
      if (invoiceData?.pendingamt) {
         setPendingReturnAdjustment(invoiceData?.pendingamt);
      }
   }, [invoiceData?.pendingamt]);

   return (
      <Modal isOpen={isOpen} onClose={onClose} size={size} closeOnOverlayClick={false}>
         <ModalOverlay />
         <ModalContent maxH={maxHeight}>
            <ModalHeader>
               <Flex justifyContent="space-between" alignItems="center">
                  <Text fontSize="xl" fontWeight="bold">
                     {title}
                  </Text>
                  <Box mr={5}>
                     <Flex justifyContent="start" alignItems="center" gap={4}>
                        <Text fontSize="xl" fontWeight="bold">
                           Pending Amt: {invoiceData?.pendingamt?.toFixed(2)}
                        </Text>
                        <Text fontSize="xl" fontWeight="bold">
                           Adjust Amt: {adjustedAmount?.toFixed(2)}
                        </Text>
                     </Flex>
                  </Box>
               </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto">
               <Show when={!isLoading}>
                  <Box>
                     <Box
                        bg={bgColor}
                        borderRadius="lg"
                        overflow="hidden"
                        boxShadow="sm"
                        border="1px"
                        borderColor={borderColor}>
                        <Table variant="simple" size="md">
                           <Thead bg={headerBg}>
                              <Tr>
                                 <Th width="40px" py={4}>
                                    <Checkbox
                                       isChecked={selectAll}
                                       onChange={handleSelectAll}
                                       colorScheme="blue"
                                       isDisabled={!pendingAdjustment.length}
                                       isIndeterminate
                                    />
                                 </Th>
                                 {columns.map((column) => (
                                    <Th key={column.id}>
                                       <HStack spacing={2}>
                                          <Text>{column.header}</Text>
                                       </HStack>
                                    </Th>
                                 ))}
                              </Tr>
                           </Thead>
                           <Tbody>
                              {newPendingAdjustment.length > 0 ? (
                                 newPendingAdjustment.map((item) => (
                                    <Tr
                                       key={item.id}
                                       bg={selectedRows.has(item.id) ? selectedRowBg : 'transparent'}
                                       _hover={{ bg: hoverBg }}
                                       borderBottom="1px"
                                       borderColor={borderColor}>
                                       <Td>
                                          <Checkbox
                                             isChecked={selectedRows.has(item.id)}
                                             onChange={(e) => handleRowSelect(item.id, e.target.checked)}
                                             colorScheme="blue"
                                          />
                                       </Td>
                                       {columns.map((column) => (
                                          <Td key={`${item.id}-${column.id}`}>
                                             <Text>{getCellValue(item, column.accessor)}</Text>
                                          </Td>
                                       ))}
                                    </Tr>
                                 ))
                              ) : (
                                 <Tr>
                                    <Td colSpan={columns.length + 1} textAlign="center" py={10}>
                                       <Text>No transactions found</Text>
                                    </Td>
                                 </Tr>
                              )}
                           </Tbody>
                        </Table>
                     </Box>
                  </Box>
               </Show>
            </ModalBody>
            <ModalFooter>
               <Button variant="ghost" mr={3} onClick={onClose}>
                  Close
               </Button>
               {newPendingAdjustment.length > 0 && (
                  <Button colorScheme="blue" onClick={() => handleSave()} disabled={isLoading} isLoading={isLoading}>
                     Save {selectedRows.size > 0 ? `(${selectedRows.size})` : null}
                  </Button>
               )}
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default TransactionDetailModal;
